import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../Home/ThemeContext';
import { 
  TrendingUp, Globe, ArrowRight, ArrowLeft, Database, 
  Rocket, ShieldCheck, Microscope, HeartHandshake, CheckCircle2,
  Zap, Layers, Users
} from 'lucide-react';

const PlatformFeatureDetail = () => {
  const { featureId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [featureId]);

  // Expanded Data Source for Detail Pages
  const featureData = {
    'precision-matching': {
      title: 'Precision Matching',
      subtitle: 'Find Your Perfect Academic Fit',
      description: 'Our proprietary AI algorithms analyze over 50 data points—from your academic grades and test scores to your extracurricular interests and budget—to recommend universities where you are most likely to succeed.',
      icon: <Microscope className="w-12 h-12" />,
      gradient: 'from-blue-600 to-indigo-500',
      benefits: [
        'AI-Driven Compatibility Scores',
        'Budget & Scholarship Alignment',
        'Cultural & Lifestyle Fit Analysis',
        'Career Outcome Prediction'
      ]
    },
    'smart-analytics': {
      title: 'Smart Analytics',
      subtitle: 'Data-Backed Decision Making',
      description: 'Stop guessing. Use real-time data to understand admission trends, acceptance rates, and alumni success stories. Our analytics dashboard gives you the competitive edge by showing you exactly where you stand.',
      icon: <TrendingUp className="w-12 h-12" />,
      gradient: 'from-purple-600 to-fuchsia-500',
      benefits: [
        'Historical Acceptance Rates',
        'Live Application Tracking',
        'Competitor Analysis',
        'ROI & Salary Projections'
      ]
    },
    'unified-dashboard': {
      title: 'Unified Dashboard',
      subtitle: 'Your Entire Journey in One Place',
      description: 'Say goodbye to scattered emails and missed deadlines. Our Unified Dashboard centralizes every document, application status, and counselor communication into one intuitive interface.',
      icon: <Database className="w-12 h-12" />,
      gradient: 'from-orange-500 to-red-600',
      benefits: [
        'Document Vault & Auto-fill',
        'Real-time Status Notifications',
        'Integrated Chat with Counselors',
        'Timeline & Deadline Manager'
      ]
    },
    'verified-network': {
      title: 'Verified Network',
      subtitle: 'Direct Access to Global Institutions',
      description: 'We don’t just list universities; we partner with them. Our verified network ensures your application bypasses the noise and reaches the right admissions officers directly.',
      icon: <ShieldCheck className="w-12 h-12" />,
      gradient: 'from-emerald-500 to-teal-600',
      benefits: [
        'Priority Application Review',
        'Exclusive Scholarship Pools',
        'Direct Admissions Channels',
        'Verified Institution Badges'
      ]
    },
    'human-support': {
      title: 'Human Support',
      subtitle: 'Expertise with Empathy',
      description: 'Technology is our tool, but people are our core. You get dedicated 1-on-1 mentorship from experts who have lived the international student journey and understand the nuances of visa interviews and SOPs.',
      icon: <HeartHandshake className="w-12 h-12" />,
      gradient: 'from-pink-500 to-rose-600',
      benefits: [
        '1-on-1 Dedicated Counselor',
        'Unlimited SOP & Essay Reviews',
        'Mock Visa Interviews',
        '24/7 Emergency Support'
      ]
    },
    'global-gateway': {
      title: 'Global Gateway',
      subtitle: 'Beyond Just Admissions',
      description: 'Our support doesn’t end with an acceptance letter. The Global Gateway program helps you settle in, finding accommodation, connecting with local alumni, and navigating part-time job markets.',
      icon: <Globe className="w-12 h-12" />,
      gradient: 'from-blue-400 to-cyan-500',
      benefits: [
        'Pre-departure Briefings',
        'Accommodation Assistance',
        'Sim Card & Forex Support',
        'Local Alumni Connections'
      ]
    }
  };

  const feature = featureData[featureId];

  // Fallback if ID is invalid
  if (!feature) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? 'bg-[#030712] text-white' : 'bg-gray-50 text-black'}`}>
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">Feature Not Found</h1>
          <button onClick={() => navigate(-1)} className="text-blue-500 hover:underline">Go Back</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-500 ${isDarkMode ? 'bg-[#0f172a]' : 'bg-gray-50'}`}>
      
      {/* --- HERO SECTION --- */}
      <div className={`relative pt-32 pb-20 overflow-hidden`}>
        <div className={`absolute inset-0 opacity-10 bg-gradient-to-br ${feature.gradient}`} />
        {/* Background Mesh */}
        <div className={`absolute inset-0 ${isDarkMode ? 'opacity-20' : 'opacity-10'}`}>
            <div className="absolute inset-0" style={{ backgroundImage: `radial-gradient(${isDarkMode ? '#fff' : '#000'} 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />
        </div>

        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <button 
            onClick={() => navigate(-1)} 
            className={`flex items-center gap-2 mb-8 font-bold text-sm transition-colors ${isDarkMode ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-black'}`}
          >
            <ArrowLeft size={16} /> Back to Platform
          </button>

          <div className="flex flex-col md:flex-row gap-10 items-center">
            {/* Icon Box */}
            <div className={`p-8 rounded-[2.5rem] shadow-2xl bg-gradient-to-br ${feature.gradient} text-white`}>
              <div className="scale-150">{feature.icon}</div>
            </div>

            {/* Text Content */}
            <div className="flex-1">
              <div className={`inline-block px-4 py-1 rounded-full text-xs font-black uppercase tracking-widest mb-4 border ${isDarkMode ? 'border-white/20 text-blue-300 bg-blue-900/20' : 'border-blue-200 text-blue-600 bg-blue-50'}`}>
                Feature Spotlight
              </div>
              <h1 className={`text-4xl md:text-6xl font-black mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {feature.title}
              </h1>
              <p className={`text-xl md:text-2xl font-light ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                {feature.subtitle}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* --- DETAIL CONTENT --- */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          
          {/* Main Description */}
          <div className="lg:col-span-2 space-y-8">
            <div className={`p-8 rounded-3xl border ${isDarkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200 shadow-xl'}`}>
              <h2 className={`text-2xl font-bold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Why this matters</h2>
              <p className={`text-lg leading-relaxed ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {feature.description}
              </p>
            </div>

            {/* Benefits Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {feature.benefits.map((benefit, idx) => (
                <div key={idx} className={`p-6 rounded-2xl flex items-start gap-4 border transition-all hover:-translate-y-1 ${isDarkMode ? 'bg-gray-900 border-gray-800 hover:border-gray-700' : 'bg-white border-gray-100 shadow-md hover:shadow-lg'}`}>
                  <div className={`mt-1 p-1 rounded-full bg-gradient-to-r ${feature.gradient}`}>
                    <CheckCircle2 size={16} className="text-white" />
                  </div>
                  <span className={`font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{benefit}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Sidebar CTA */}
          <div className="lg:col-span-1">
            <div className={`sticky top-24 p-8 rounded-[2.5rem] text-center overflow-hidden relative ${isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-gray-900 text-white'}`}>
              <div className={`absolute inset-0 opacity-20 bg-gradient-to-br ${feature.gradient}`} />
              
              <div className="relative z-10">
                <Rocket className="w-12 h-12 mx-auto mb-6 text-white" />
                <h3 className={`text-2xl font-black mb-4 ${isDarkMode ? 'text-white' : 'text-white'}`}>Ready to experience {feature.title}?</h3>
                <p className="text-sm opacity-80 mb-8">Join thousands of students who have simplified their journey with Vidyazon.</p>
                
                <button onClick={() => navigate('/signup')} className="w-full py-4 bg-white text-black font-bold rounded-xl hover:scale-105 transition-transform shadow-lg flex items-center justify-center gap-2">
                  Get Started <ArrowRight size={16}/>
                </button>
                <p className="mt-4 text-xs opacity-60">No credit card required for consultation.</p>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* --- BOTTOM NAV (Next Feature) --- */}
      <div className={`border-t ${isDarkMode ? 'border-gray-800' : 'border-gray-200'} py-12`}>
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className={`text-sm font-bold uppercase tracking-widest mb-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>Keep Exploring</p>
          <button onClick={() => navigate('/student/programs')} className={`text-3xl md:text-5xl font-black hover:text-transparent bg-clip-text bg-gradient-to-r ${feature.gradient} transition-all`}>
            Explore All Programs &rarr;
          </button>
        </div>
      </div>

    </div>
  );
};

export default PlatformFeatureDetail;